import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RestaurantInfoService } from '../appServices/restaurant-info.service';
import { StorageService } from '../appServices/storage.service';
import { StorageKeys } from '../appServices/storage-keys';

@Injectable({
    providedIn: 'root'
})
export class RestaurantBasicInfoStorage {
    restaurantBasicInfo = new BehaviorSubject(null);
    // Store cache version (timestamp) for image URL cache busting
    private cacheVersion: number = Date.now();
    
    // Cache expiry time: 1 hour (3600000 milliseconds)
    private readonly CACHE_EXPIRY_MS = 60 * 60 * 1000;
    
    constructor(
        private restaurantInfoService: RestaurantInfoService,
        private storageService: StorageService
    ) {
        this.getBasicInfo();
    }

    /**
     * Get basic info with cache expiry check
     * Automatically refreshes if cache is older than 1 hour
     */
    async getBasicInfo() {
        const cached = await this.storageService.getItem<any>(StorageKeys.BASIC_INFO);
        
        if (!cached) {
            // No cache, fetch from API
            this.setRestaurantBasicInfoFromApi();
            return;
        }
        
        // Check if cache has timestamp (new format) or is old format
        const cacheTimestamp = cached.timestamp || 0;
        const cacheAge = Date.now() - cacheTimestamp;
        
        if (cacheTimestamp === 0 || cacheAge > this.CACHE_EXPIRY_MS) {
            // Cache expired or old format, refresh from API
            console.log('Basic info cache expired, refreshing from API...');
            this.setRestaurantBasicInfoFromApi();
        } else {
            // Cache is fresh, use cached data
            // Store cache version for image URL cache busting
            this.cacheVersion = cacheTimestamp;
            // Remove timestamp before emitting (keep original data structure)
            const { timestamp, ...basicInfoData } = cached;
            this.restaurantBasicInfo.next(basicInfoData);
        }
    }

    /**
     * Set basic info from API with timestamp for cache expiry
     */
    setRestaurantBasicInfoFromApi() {
        this.restaurantInfoService.getBasicInfo().subscribe({
            next: async (res) => {
                try {
                    // Add timestamp for cache expiry check
                    const dataWithTimestamp = {
                        ...res,
                        timestamp: Date.now()
                    };
                    await this.storageService.setItem(StorageKeys.BASIC_INFO, dataWithTimestamp);
                    
                    // Update cache version for image URL cache busting
                    this.cacheVersion = dataWithTimestamp.timestamp;
                    
                    // Emit without timestamp (keep original data structure)
                    this.restaurantBasicInfo.next(res);
                    console.log('Basic info refreshed from API');
                } catch (error) {
                    console.error('Failed to save basic info:', error);
                }
            },
            error: (err) => {
                console.error('Failed to fetch basic info from API:', err);
            }
        });
    }

    /**
     * Force refresh basic info (logo) from API
     * Used by manual refresh button
     * Returns a promise that resolves when refresh is complete
     */
    async refreshBasicInfo(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.restaurantInfoService.getBasicInfo().subscribe({
                next: async (res) => {
                    try {
                        // Add timestamp for cache expiry check
                        const dataWithTimestamp = {
                            ...res,
                            timestamp: Date.now()
                        };
                        await this.storageService.setItem(StorageKeys.BASIC_INFO, dataWithTimestamp);
                        
                        // Update cache version for image URL cache busting
                        this.cacheVersion = dataWithTimestamp.timestamp;
                        
                        // Emit without timestamp (keep original data structure)
            this.restaurantBasicInfo.next(res);
                        console.log('Basic info manually refreshed from API');
                        resolve();
                    } catch (error) {
                        console.error('Failed to save refreshed basic info:', error);
                        reject(error);
                    }
                },
                error: (err) => {
                    console.error('Failed to refresh basic info:', err);
                    reject(err);
                }
            });
        });
    }

    /**
     * Get cache version (timestamp) for image URL cache busting
     * This prevents browser from caching images from CDN
     */
    getCacheVersion(): number {
        return this.cacheVersion;
    }

    async clearInfo() {
        await this.storageService.removeItem(StorageKeys.BASIC_INFO);
        this.cacheVersion = Date.now(); // Reset cache version
        this.restaurantBasicInfo.next(null);
    }
}